import {
  ReportCreateSchema,
  ReportSaveSchema,
  ReportSchema,
  type ReportType,
  ReportUpdateSchema,
  SnapshotSchema,
  type SnapshotType
} from "@/types/report";
import { makeApi, makeErrors } from "@zodios/core";
import z from "zod";

const errors = makeErrors([
  {
    status: 404,
    description: "Report not found",
    schema: z.object({
      error: z.object({
        reportId: z.number(),
        code: z.string(),
        message: z.string()
      })
    })
  },
  {
    status: 401,
    description: "Unauthorized access",
    schema: z.object({
      code: z.string(),
      message: z.string()
    })
  },
  {
    status: "default",
    description: "Default error",
    schema: z.object({
      error: z.object({
        code: z.string(),
        message: z.string()
      })
    })
  }
]);

export const reportApi = makeApi([
  {
    alias: "createReport",
    method: "post",
    path: "/blueprint",
    response: ReportSchema,
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReportCreateSchema
      }
    ],
    errors
  },
  {
    alias: "getReport",
    method: "get",
    path: "/blueprint/:id",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: ReportSchema,
    errors
  },
  {
    alias: "getAllReports",
    method: "get",
    path: "/blueprint/search",
    response: z.array(ReportSchema),
    errors
  },
  {
    alias: "updateReport",
    method: "patch",
    path: "/blueprint/:id",
    response: ReportSchema,
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      },
      {
        name: "body",
        type: "Body",
        schema: ReportUpdateSchema
      }
    ],
    errors
  },
  {
    alias: "trashReport",
    method: "patch",
    path: "/blueprint/:id/trash",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: z.object({
      message: z.string()
    }),
    errors
  },
  {
    alias: "restoreReport",
    method: "patch",
    path: "/blueprint/:id/restore",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: z.object({
      message: z.string()
    }),
    errors
  },
  {
    alias: "saveSnapshot",
    method: "post",
    path: "/blueprint/:id/snapshot/save",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReportSaveSchema
      },
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: SnapshotSchema,
    errors
  },
  {
    alias: "getSnapshots",
    method: "get",
    path: "/blueprint/:id/snapshot",
    response: z.array(SnapshotSchema),
    errors
  },
  {
    alias: "starReport",
    method: "post",
    path: "/blueprint/:id/star",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: z.null(),
    errors
  },
  {
    alias: "unstarReport",
    method: "post",
    path: "/blueprint/:id/unstar",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string()
      }
    ],
    response: z.null(),
    errors
  }
  // {//使ってない
  //   alias: "createOutlineStream",
  //   method: "post",
  //   path: "/blueprint/:id/outline/generate/stream",
  //   responseType: "blob",
  //   response: OutlineResponseSchema,
  //   errors
  // }
]);

//outlineをstreamAPIで取得
interface StreamData {
  data: string;
}

type StreamOutlineProps = {
  generatingReport: ReportType | null;
  items: { id: number; text: string }[];
  setItems: React.Dispatch<React.SetStateAction<{ id: number; text: string }[]>>;
  add: () => void;
};

export const streamOutline = async (props: StreamOutlineProps) => {
  if (!props.generatingReport) return;
  const res = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/blueprint/${props.generatingReport.id}/outline/generate/stream`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      mode: "cors"
    }
  );
  const reader = res.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let sectionIdx = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const lines = decoder.decode(value);
    const jsons = lines
      .split("\n")
      .map(line => line.trim())
      .filter(s => s);

    for (const json of jsons) {
      if (json.indexOf("outline-generate") !== -1) {
        continue;
      }
      const parsed: StreamData = JSON.parse(json.split("data:")[1]);

      for (let i = 0; i < parsed.data.length; i++) {
        if (String(parsed.data).charAt(i) === ",") {
          sectionIdx++;
          if (sectionIdx >= props.items.length) {
            //こうしないと入れていたアイテムが空になる
            props.add();
          }
        } else {
          props.setItems(prev => {
            const updatedItems = [...prev];
            updatedItems[sectionIdx] = {
              ...updatedItems[sectionIdx],
              text: updatedItems[sectionIdx].text + parsed.data.charAt(i)
            };
            return [...updatedItems];
          });
        }
      }
    }
  }
};

type StreamReportProps = {
  generatingReport: ReportType | null;
  setGeneratedSnapshot: React.Dispatch<React.SetStateAction<SnapshotType | undefined>>;
  onComplete?: (snapshot: SnapshotType) => void;
};

export const streamReport = async (props: StreamReportProps) => {
  if (!props.generatingReport) return;

  try {
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/blueprint/${props.generatingReport.id}/snapshot/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        mode: "cors"
      }
    );

    if (!res.ok) {
      if (res.status === 422) {
        console.error("Credit shortage error");
        return 422;
      }
      console.error("Stream API error:", res.status, res.statusText);
      return res.status;
    }

    const reader = res.body?.getReader();
    if (!reader) {
      console.error("Could not get reader from response");
      return 500;
    }

    // Process the stream data
    return processStream(reader, props);
  } catch (error) {
    console.error("Fetch error:", error);
    return 500;
  }
};

async function processStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  props: StreamReportProps
): Promise<number> {
  const decoder = new TextDecoder();
  let fullHtml = "";

  const initialSnapshot: SnapshotType = {
    id: -1,
    blueprint_id: -1,
    outline_id: null,
    html: "",
    lexical_json: "",
    created_at: "",
    updated_at: "",
    blueprint: null
  };

  props.setGeneratedSnapshot(initialSnapshot);

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (fullHtml) {
          const finalSnapshot: SnapshotType = {
            ...initialSnapshot,
            html: fullHtml
          };

          props.setGeneratedSnapshot(finalSnapshot);

          if (props.onComplete) {
            props.onComplete(finalSnapshot);
          }
        }
        break;
      }

      if (!value) continue;

      const lines = decoder.decode(value);
      const jsons = lines
        .split("\n")
        .map(line => line.trim())
        .filter(s => s);

      for (const json of jsons) {
        if (json.indexOf("snapshot-generate") !== -1) continue;

        try {
          const parsedJson = json.split("data:")[1];
          if (!parsedJson) continue;

          const parsed: StreamData = JSON.parse(parsedJson);
          fullHtml += parsed.data;

          props.setGeneratedSnapshot(prev => ({
            ...(prev || initialSnapshot),
            html: fullHtml
          }));
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }
    }

    return 200;
  } catch (error) {
    console.error("Error during stream processing:", error);
    return 500;
  }
}
